/**
 * SupportCenterTab.js - Owner Support Dashboard
 * Comprehensive support hub with tickets, voicemails, call logs, chat, and online status
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Headphones, MessageSquare, Phone, PhoneIncoming, PhoneOutgoing, PhoneMissed,
  Mail, Voicemail, Users, Clock, CheckCircle, AlertCircle, XCircle,
  Play, Pause, Trash2, Eye, Send, RefreshCw, Filter, Search,
  Circle, User, Video, PhoneCall, MessageCircle, Settings, Volume2,
  ArrowUpRight, ArrowDownLeft, Calendar, FileText, ChevronDown, ChevronUp,
  MoreVertical, Download, Archive, Star, Flag, Check, X, Bell
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import api from '../../services/api';
import notificationService from '../../services/notificationService';

const SupportCenterTab = ({ onStartChat, onStartCall }) => {
  // Sub-tab state
  const [activeSubTab, setActiveSubTab] = useState('dashboard');
  
  // Dashboard stats
  const [dashboardStats, setDashboardStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);
  const [prevStats, setPrevStats] = useState(null);
  
  // Tickets state
  const [tickets, setTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [ticketFilter, setTicketFilter] = useState('open');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [ticketResponse, setTicketResponse] = useState('');
  const [sendingResponse, setSendingResponse] = useState(false);
  
  // Voicemails state
  const [voicemails, setVoicemails] = useState([]);
  const [voicemailsLoading, setVoicemailsLoading] = useState(false);
  const [playingVoicemail, setPlayingVoicemail] = useState(null);
  const audioRef = useRef(null);
  
  // Call logs state
  const [callLogs, setCallLogs] = useState([]);
  const [callLogsLoading, setCallLogsLoading] = useState(false);
  const [callLogFilter, setCallLogFilter] = useState('all');
  
  // Online users state
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [onlineLoading, setOnlineLoading] = useState(false);
  
  // Settings state
  const [settings, setSettings] = useState(null);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);

  // Fetch dashboard stats
  const fetchDashboard = useCallback(async () => {
    setLoadingStats(true);
    try {
      const res = await api.get('/support/dashboard');
      const newStats = res.data;
      
      // Check for new items and show notifications
      if (prevStats && notificationService.getPermissionStatus() === 'granted') {
        // New tickets
        if (newStats.stats?.open_tickets > prevStats.stats?.open_tickets) {
          const newTicket = newStats.recent_tickets?.[0];
          if (newTicket) {
            notificationService.showSupportTicketNotification(newTicket);
            toast.info(`New support ticket from ${newTicket.user_name}`);
          }
        }
        
        // New voicemails
        if (newStats.stats?.unlistened_voicemails > prevStats.stats?.unlistened_voicemails) {
          const newVoicemail = newStats.recent_voicemails?.find(v => !v.listened);
          if (newVoicemail) {
            notificationService.showVoicemailNotification(newVoicemail);
            toast.info(`New voicemail from ${newVoicemail.sender_name}`);
          }
        }
      }
      
      setPrevStats(newStats);
      setDashboardStats(newStats);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    } finally {
      setLoadingStats(false);
    }
  }, [prevStats]);

  // Fetch tickets
  const fetchTickets = useCallback(async () => {
    setTicketsLoading(true);
    try {
      const params = ticketFilter !== 'all' ? `?status=${ticketFilter}` : '';
      const res = await api.get(`/support/tickets${params}`);
      setTickets(res.data.tickets || []);
    } catch (error) {
      console.error('Failed to fetch tickets:', error);
    } finally {
      setTicketsLoading(false);
    }
  }, [ticketFilter]);

  // Fetch voicemails
  const fetchVoicemails = useCallback(async () => {
    setVoicemailsLoading(true);
    try {
      const res = await api.get('/support/voicemails?direction=received');
      setVoicemails(res.data.voicemails || []);
    } catch (error) {
      console.error('Failed to fetch voicemails:', error);
    } finally {
      setVoicemailsLoading(false);
    }
  }, []);

  // Fetch call logs
  const fetchCallLogs = useCallback(async () => {
    setCallLogsLoading(true);
    try {
      const params = callLogFilter !== 'all' ? `?status=${callLogFilter}` : '';
      const res = await api.get(`/support/call-logs${params}`);
      setCallLogs(res.data.calls || []);
    } catch (error) {
      console.error('Failed to fetch call logs:', error);
    } finally {
      setCallLogsLoading(false);
    }
  }, [callLogFilter]);

  // Fetch online users
  const fetchOnlineUsers = useCallback(async () => {
    setOnlineLoading(true);
    try {
      const res = await api.get('/support/presence');
      setOnlineUsers(res.data.users || []);
    } catch (error) {
      console.error('Failed to fetch online users:', error);
    } finally {
      setOnlineLoading(false);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchDashboard();
    fetchOnlineUsers();
    
    // Poll for presence updates every 30 seconds
    const interval = setInterval(() => {
      fetchOnlineUsers();
    }, 30000);
    
    return () => clearInterval(interval);
  }, [fetchDashboard, fetchOnlineUsers]);

  // Fetch data based on active sub-tab
  useEffect(() => {
    if (activeSubTab === 'tickets') fetchTickets();
    if (activeSubTab === 'voicemails') fetchVoicemails();
    if (activeSubTab === 'calls') fetchCallLogs();
    if (activeSubTab === 'online') fetchOnlineUsers();
  }, [activeSubTab, fetchTickets, fetchVoicemails, fetchCallLogs, fetchOnlineUsers]);

  // Handle ticket response
  const handleSendResponse = async () => {
    if (!ticketResponse.trim() || !selectedTicket) return;
    
    setSendingResponse(true);
    try {
      await api.post(`/support/tickets/${selectedTicket.id}/respond?response=${encodeURIComponent(ticketResponse)}`);
      toast.success('Response sent');
      setTicketResponse('');
      setShowTicketModal(false);
      fetchTickets();
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to send response');
    } finally {
      setSendingResponse(false);
    }
  };

  // Update ticket status
  const handleUpdateTicketStatus = async (ticketId, status) => {
    try {
      await api.put(`/support/tickets/${ticketId}`, { status });
      toast.success(`Ticket marked as ${status}`);
      fetchTickets();
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to update ticket');
    }
  };

  // Play/pause voicemail
  const handlePlayVoicemail = async (voicemail) => {
    if (playingVoicemail === voicemail.id) {
      audioRef.current?.pause();
      setPlayingVoicemail(null);
    } else {
      if (audioRef.current) {
        audioRef.current.src = voicemail.file_url;
        audioRef.current.play();
        setPlayingVoicemail(voicemail.id);
        audioRef.current.onended = () => setPlayingVoicemail(null);
      }
      
      // Mark as listened
      if (!voicemail.listened) {
        try {
          await api.post(`/support/voicemails/${voicemail.id}/listened`);
          fetchVoicemails();
          fetchDashboard();
        } catch (error) {
          console.error('Failed to mark as listened:', error);
        }
      }
    }
  };

  // Delete voicemail
  const handleDeleteVoicemail = async (voicemailId) => {
    if (!confirm('Delete this voicemail?')) return;
    
    try {
      await api.delete(`/support/voicemails/${voicemailId}`);
      toast.success('Voicemail deleted');
      fetchVoicemails();
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to delete voicemail');
    }
  };

  // Format duration
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Format date/time
  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString([], { 
      month: 'short', 
      day: 'numeric', 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  // Get priority color
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'bg-red-500';
      case 'high': return 'bg-orange-500';
      case 'normal': return 'bg-blue-500';
      case 'low': return 'bg-gray-500';
      default: return 'bg-gray-500';
    }
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'text-yellow-500';
      case 'in_progress': return 'text-blue-500';
      case 'resolved': return 'text-green-500';
      case 'closed': return 'text-gray-500';
      default: return 'text-gray-500';
    }
  };

  // Sub-tabs configuration
  const subTabs = [
    { id: 'dashboard', label: 'Dashboard', icon: Headphones },
    { id: 'tickets', label: 'Support Tickets', icon: Mail },
    { id: 'voicemails', label: 'Voicemails', icon: Voicemail },
    { id: 'calls', label: 'Call Logs', icon: Phone },
    { id: 'online', label: 'Online Users', icon: Users },
  ];

  return (
    <div className="space-y-6" data-testid="support-center-tab">
      {/* Sub-tabs */}
      <div className="flex flex-wrap gap-2 border-b border-[var(--border-subtle)] pb-4">
        {subTabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveSubTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
              activeSubTab === tab.id
                ? 'bg-[#D4AF37] text-black font-medium'
                : 'bg-[var(--background-elevated)] text-[var(--text-secondary)] hover:bg-[var(--background-paper)]'
            }`}
            data-testid={`support-subtab-${tab.id}`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
        
        <button
          onClick={() => setShowSettingsModal(true)}
          className="ml-auto p-2 rounded-lg bg-[var(--background-elevated)] hover:bg-[var(--background-paper)]"
          data-testid="support-settings-btn"
        >
          <Settings className="w-5 h-5" />
        </button>
      </div>

      {/* Dashboard View */}
      {activeSubTab === 'dashboard' && (
        <div className="space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-[var(--background-elevated)] rounded-xl p-4 border border-[var(--border-subtle)]"
            >
              <div className="flex items-center justify-between">
                <div className="p-2 bg-yellow-500/20 rounded-lg">
                  <Mail className="w-5 h-5 text-yellow-500" />
                </div>
                <span className="text-2xl font-bold text-[#D4AF37]">
                  {dashboardStats?.stats?.open_tickets || 0}
                </span>
              </div>
              <p className="text-sm text-[var(--text-muted)] mt-2">Open Tickets</p>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-[var(--background-elevated)] rounded-xl p-4 border border-[var(--border-subtle)]"
            >
              <div className="flex items-center justify-between">
                <div className="p-2 bg-red-500/20 rounded-lg">
                  <Voicemail className="w-5 h-5 text-red-500" />
                </div>
                <span className="text-2xl font-bold text-[#D4AF37]">
                  {dashboardStats?.stats?.unlistened_voicemails || 0}
                </span>
              </div>
              <p className="text-sm text-[var(--text-muted)] mt-2">New Voicemails</p>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-[var(--background-elevated)] rounded-xl p-4 border border-[var(--border-subtle)]"
            >
              <div className="flex items-center justify-between">
                <div className="p-2 bg-orange-500/20 rounded-lg">
                  <PhoneMissed className="w-5 h-5 text-orange-500" />
                </div>
                <span className="text-2xl font-bold text-[#D4AF37]">
                  {dashboardStats?.stats?.missed_calls || 0}
                </span>
              </div>
              <p className="text-sm text-[var(--text-muted)] mt-2">Missed Calls</p>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-[var(--background-elevated)] rounded-xl p-4 border border-[var(--border-subtle)]"
            >
              <div className="flex items-center justify-between">
                <div className="p-2 bg-green-500/20 rounded-lg">
                  <Users className="w-5 h-5 text-green-500" />
                </div>
                <span className="text-2xl font-bold text-[#D4AF37]">
                  {dashboardStats?.stats?.online_users || 0}
                </span>
              </div>
              <p className="text-sm text-[var(--text-muted)] mt-2">Users Online</p>
            </motion.div>
          </div>

          {/* Recent Activity Grid */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Recent Tickets */}
            <div className="bg-[var(--background-elevated)] rounded-xl p-4 border border-[var(--border-subtle)]">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold flex items-center gap-2">
                  <Mail className="w-4 h-4 text-[#D4AF37]" />
                  Recent Tickets
                </h3>
                <button 
                  onClick={() => setActiveSubTab('tickets')}
                  className="text-sm text-[#D4AF37] hover:underline"
                >
                  View All
                </button>
              </div>
              
              <div className="space-y-3">
                {dashboardStats?.recent_tickets?.length > 0 ? (
                  dashboardStats.recent_tickets.map(ticket => (
                    <div 
                      key={ticket.id}
                      className="flex items-center justify-between p-3 bg-[var(--background-paper)] rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-2 h-2 rounded-full ${getPriorityColor(ticket.priority)}`} />
                        <div>
                          <p className="text-sm font-medium truncate max-w-[200px]">{ticket.subject}</p>
                          <p className="text-xs text-[var(--text-muted)]">{ticket.user_name}</p>
                        </div>
                      </div>
                      <span className={`text-xs capitalize ${getStatusColor(ticket.status)}`}>
                        {ticket.status?.replace('_', ' ')}
                      </span>
                    </div>
                  ))
                ) : (
                  <p className="text-center text-[var(--text-muted)] py-4">No recent tickets</p>
                )}
              </div>
            </div>

            {/* Recent Voicemails */}
            <div className="bg-[var(--background-elevated)] rounded-xl p-4 border border-[var(--border-subtle)]">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold flex items-center gap-2">
                  <Voicemail className="w-4 h-4 text-[#D4AF37]" />
                  Recent Voicemails
                </h3>
                <button 
                  onClick={() => setActiveSubTab('voicemails')}
                  className="text-sm text-[#D4AF37] hover:underline"
                >
                  View All
                </button>
              </div>
              
              <div className="space-y-3">
                {dashboardStats?.recent_voicemails?.length > 0 ? (
                  dashboardStats.recent_voicemails.map(vm => (
                    <div 
                      key={vm.id}
                      className="flex items-center justify-between p-3 bg-[var(--background-paper)] rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          vm.listened ? 'bg-gray-600' : 'bg-[#D4AF37]/20'
                        }`}>
                          <Voicemail className={`w-4 h-4 ${vm.listened ? 'text-gray-400' : 'text-[#D4AF37]'}`} />
                        </div>
                        <div>
                          <p className="text-sm font-medium">{vm.sender_name}</p>
                          <p className="text-xs text-[var(--text-muted)]">
                            {formatDuration(vm.duration_seconds)} • {formatDateTime(vm.created_at)}
                          </p>
                        </div>
                      </div>
                      {!vm.listened && (
                        <span className="w-2 h-2 bg-[#D4AF37] rounded-full" />
                      )}
                    </div>
                  ))
                ) : (
                  <p className="text-center text-[var(--text-muted)] py-4">No voicemails</p>
                )}
              </div>
            </div>
          </div>

          {/* Online Users Quick View */}
          <div className="bg-[var(--background-elevated)] rounded-xl p-4 border border-[var(--border-subtle)]">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold flex items-center gap-2">
                <Circle className="w-3 h-3 text-green-500 fill-green-500" />
                Online Now
              </h3>
              <button 
                onClick={() => setActiveSubTab('online')}
                className="text-sm text-[#D4AF37] hover:underline"
              >
                View All
              </button>
            </div>
            
            <div className="flex flex-wrap gap-3">
              {onlineUsers.filter(u => u.status === 'online').slice(0, 8).map(user => (
                <div 
                  key={user.user_id}
                  className="flex items-center gap-2 p-2 bg-[var(--background-paper)] rounded-lg"
                >
                  <div className="relative">
                    <div className="w-8 h-8 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                      <User className="w-4 h-4 text-[#D4AF37]" />
                    </div>
                    <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-[var(--background-paper)]" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">{user.user_name}</p>
                    <p className="text-xs text-[var(--text-muted)] capitalize">{user.user_role}</p>
                  </div>
                  <div className="flex gap-1 ml-2">
                    <button 
                      onClick={() => onStartChat?.(user)}
                      className="p-1.5 rounded-lg bg-[#D4AF37]/20 hover:bg-[#D4AF37]/30 text-[#D4AF37]"
                      title="Start Chat"
                    >
                      <MessageSquare className="w-4 h-4" />
                    </button>
                    <button 
                      onClick={() => onStartCall?.(user, 'voice')}
                      className="p-1.5 rounded-lg bg-green-500/20 hover:bg-green-500/30 text-green-500"
                      title="Voice Call"
                    >
                      <Phone className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
              
              {onlineUsers.filter(u => u.status === 'online').length === 0 && (
                <p className="text-[var(--text-muted)]">No users online</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Tickets View */}
      {activeSubTab === 'tickets' && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex gap-2">
              {['all', 'open', 'in_progress', 'resolved', 'closed'].map(status => (
                <button
                  key={status}
                  onClick={() => setTicketFilter(status)}
                  className={`px-3 py-1.5 rounded-lg text-sm transition-all ${
                    ticketFilter === status
                      ? 'bg-[#D4AF37] text-black'
                      : 'bg-[var(--background-paper)] text-[var(--text-secondary)] hover:bg-[var(--background-elevated)]'
                  }`}
                >
                  {status === 'all' ? 'All' : status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </button>
              ))}
            </div>
            
            <button
              onClick={fetchTickets}
              className="ml-auto p-2 rounded-lg bg-[var(--background-paper)] hover:bg-[var(--background-elevated)]"
            >
              <RefreshCw className={`w-4 h-4 ${ticketsLoading ? 'animate-spin' : ''}`} />
            </button>
          </div>

          {/* Tickets List */}
          <div className="space-y-3">
            {ticketsLoading ? (
              <div className="flex justify-center py-8">
                <div className="w-8 h-8 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : tickets.length > 0 ? (
              tickets.map(ticket => (
                <motion.div
                  key={ticket.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="bg-[var(--background-elevated)] rounded-xl p-4 border border-[var(--border-subtle)]"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className={`w-2 h-2 rounded-full mt-2 ${getPriorityColor(ticket.priority)}`} />
                      <div>
                        <h4 className="font-semibold">{ticket.subject}</h4>
                        <p className="text-sm text-[var(--text-muted)] mt-1 line-clamp-2">{ticket.message}</p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-[var(--text-muted)]">
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {ticket.user_name}
                          </span>
                          <span className="capitalize">{ticket.user_role}</span>
                          <span>{formatDateTime(ticket.created_at)}</span>
                          {ticket.callback_requested && (
                            <span className="flex items-center gap-1 text-orange-500">
                              <PhoneCall className="w-3 h-3" />
                              Callback Requested
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-1 rounded-full capitalize ${getStatusColor(ticket.status)} bg-current/10`}>
                        {ticket.status?.replace('_', ' ')}
                      </span>
                      
                      <div className="flex gap-1">
                        <button
                          onClick={() => {
                            setSelectedTicket(ticket);
                            setShowTicketModal(true);
                          }}
                          className="p-2 rounded-lg hover:bg-[var(--background-paper)]"
                          title="View & Respond"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        
                        {ticket.status === 'open' && (
                          <button
                            onClick={() => handleUpdateTicketStatus(ticket.id, 'in_progress')}
                            className="p-2 rounded-lg hover:bg-blue-500/20 text-blue-500"
                            title="Mark In Progress"
                          >
                            <Clock className="w-4 h-4" />
                          </button>
                        )}
                        
                        {ticket.status !== 'resolved' && ticket.status !== 'closed' && (
                          <button
                            onClick={() => handleUpdateTicketStatus(ticket.id, 'resolved')}
                            className="p-2 rounded-lg hover:bg-green-500/20 text-green-500"
                            title="Mark Resolved"
                          >
                            <CheckCircle className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="text-center py-12 text-[var(--text-muted)]">
                <Mail className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No tickets found</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Voicemails View */}
      {activeSubTab === 'voicemails' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">
              Voicemails ({voicemails.length})
            </h3>
            <button
              onClick={fetchVoicemails}
              className="p-2 rounded-lg bg-[var(--background-paper)] hover:bg-[var(--background-elevated)]"
            >
              <RefreshCw className={`w-4 h-4 ${voicemailsLoading ? 'animate-spin' : ''}`} />
            </button>
          </div>

          <div className="space-y-3">
            {voicemailsLoading ? (
              <div className="flex justify-center py-8">
                <div className="w-8 h-8 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : voicemails.length > 0 ? (
              voicemails.map(vm => (
                <motion.div
                  key={vm.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className={`bg-[var(--background-elevated)] rounded-xl p-4 border ${
                    vm.listened ? 'border-[var(--border-subtle)]' : 'border-[#D4AF37]/50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <button
                        onClick={() => handlePlayVoicemail(vm)}
                        className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${
                          playingVoicemail === vm.id
                            ? 'bg-[#D4AF37] text-black'
                            : 'bg-[#D4AF37]/20 text-[#D4AF37] hover:bg-[#D4AF37]/30'
                        }`}
                      >
                        {playingVoicemail === vm.id ? (
                          <Pause className="w-5 h-5" />
                        ) : (
                          <Play className="w-5 h-5 ml-0.5" />
                        )}
                      </button>
                      
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-semibold">{vm.sender_name}</p>
                          {!vm.listened && (
                            <span className="text-xs px-2 py-0.5 bg-[#D4AF37] text-black rounded-full">New</span>
                          )}
                        </div>
                        <p className="text-sm text-[var(--text-muted)]">
                          {formatDuration(vm.duration_seconds)} • {formatDateTime(vm.created_at)}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => onStartCall?.({ user_id: vm.sender_id, user_name: vm.sender_name }, 'voice')}
                        className="p-2 rounded-lg hover:bg-green-500/20 text-green-500"
                        title="Call Back"
                      >
                        <PhoneCall className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => onStartChat?.({ user_id: vm.sender_id, user_name: vm.sender_name })}
                        className="p-2 rounded-lg hover:bg-[#D4AF37]/20 text-[#D4AF37]"
                        title="Message"
                      >
                        <MessageSquare className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteVoicemail(vm.id)}
                        className="p-2 rounded-lg hover:bg-red-500/20 text-red-500"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="text-center py-12 text-[var(--text-muted)]">
                <Voicemail className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No voicemails</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Call Logs View */}
      {activeSubTab === 'calls' && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex gap-2">
              {['all', 'connected', 'missed', 'declined'].map(status => (
                <button
                  key={status}
                  onClick={() => setCallLogFilter(status)}
                  className={`px-3 py-1.5 rounded-lg text-sm transition-all ${
                    callLogFilter === status
                      ? 'bg-[#D4AF37] text-black'
                      : 'bg-[var(--background-paper)] text-[var(--text-secondary)] hover:bg-[var(--background-elevated)]'
                  }`}
                >
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </button>
              ))}
            </div>
            
            <button
              onClick={fetchCallLogs}
              className="ml-auto p-2 rounded-lg bg-[var(--background-paper)] hover:bg-[var(--background-elevated)]"
            >
              <RefreshCw className={`w-4 h-4 ${callLogsLoading ? 'animate-spin' : ''}`} />
            </button>
          </div>

          {/* Call Logs List */}
          <div className="space-y-3">
            {callLogsLoading ? (
              <div className="flex justify-center py-8">
                <div className="w-8 h-8 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : callLogs.length > 0 ? (
              callLogs.map(call => (
                <motion.div
                  key={call.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="bg-[var(--background-elevated)] rounded-xl p-4 border border-[var(--border-subtle)]"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        call.status === 'connected' ? 'bg-green-500/20' :
                        call.status === 'missed' || call.status === 'no_answer' ? 'bg-red-500/20' :
                        'bg-gray-500/20'
                      }`}>
                        {call.direction === 'incoming' ? (
                          <PhoneIncoming className={`w-5 h-5 ${
                            call.status === 'connected' ? 'text-green-500' :
                            call.status === 'missed' || call.status === 'no_answer' ? 'text-red-500' :
                            'text-gray-500'
                          }`} />
                        ) : (
                          <PhoneOutgoing className={`w-5 h-5 ${
                            call.status === 'connected' ? 'text-green-500' :
                            call.status === 'missed' || call.status === 'no_answer' ? 'text-red-500' :
                            'text-gray-500'
                          }`} />
                        )}
                      </div>
                      
                      <div>
                        <p className="font-semibold">{call.other_party?.name || 'Unknown'}</p>
                        <div className="flex items-center gap-3 text-sm text-[var(--text-muted)]">
                          <span className="capitalize">{call.other_party?.role}</span>
                          <span>{formatDateTime(call.started_at)}</span>
                          {call.duration_seconds > 0 && (
                            <span>{formatDuration(call.duration_seconds)}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-1 rounded-full capitalize ${
                        call.status === 'connected' ? 'bg-green-500/20 text-green-500' :
                        call.status === 'missed' || call.status === 'no_answer' ? 'bg-red-500/20 text-red-500' :
                        'bg-gray-500/20 text-gray-500'
                      }`}>
                        {call.call_type === 'video' && <Video className="w-3 h-3 inline mr-1" />}
                        {call.status}
                      </span>
                      
                      <button
                        onClick={() => onStartCall?.(call.other_party, 'voice')}
                        className="p-2 rounded-lg hover:bg-green-500/20 text-green-500"
                        title="Call Back"
                      >
                        <Phone className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="text-center py-12 text-[var(--text-muted)]">
                <Phone className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No call logs</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Online Users View */}
      {activeSubTab === 'online' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold flex items-center gap-2">
              <Circle className="w-3 h-3 text-green-500 fill-green-500" />
              Online Users ({onlineUsers.filter(u => u.status === 'online').length})
            </h3>
            <button
              onClick={fetchOnlineUsers}
              className="p-2 rounded-lg bg-[var(--background-paper)] hover:bg-[var(--background-elevated)]"
            >
              <RefreshCw className={`w-4 h-4 ${onlineLoading ? 'animate-spin' : ''}`} />
            </button>
          </div>

          {/* Group by role */}
          {['worker', 'customer'].map(role => {
            const usersInRole = onlineUsers.filter(u => u.user_role === role);
            if (usersInRole.length === 0) return null;
            
            return (
              <div key={role} className="space-y-3">
                <h4 className="text-sm font-medium text-[var(--text-muted)] uppercase tracking-wider">
                  {role}s ({usersInRole.length})
                </h4>
                
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {usersInRole.map(user => (
                    <motion.div
                      key={user.user_id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="bg-[var(--background-elevated)] rounded-xl p-4 border border-[var(--border-subtle)]"
                    >
                      <div className="flex items-center gap-3">
                        <div className="relative">
                          <div className="w-12 h-12 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                            <User className="w-6 h-6 text-[#D4AF37]" />
                          </div>
                          <div className={`absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full border-2 border-[var(--background-elevated)] ${
                            user.status === 'online' ? 'bg-green-500' :
                            user.status === 'away' ? 'bg-yellow-500' :
                            user.status === 'busy' ? 'bg-red-500' :
                            'bg-gray-500'
                          }`} />
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold truncate">{user.user_name}</p>
                          <p className="text-xs text-[var(--text-muted)] capitalize">{user.status}</p>
                        </div>
                      </div>
                      
                      <div className="flex gap-2 mt-3">
                        <button
                          onClick={() => onStartChat?.(user)}
                          className="flex-1 p-2 rounded-lg bg-[#D4AF37]/20 hover:bg-[#D4AF37]/30 text-[#D4AF37] flex items-center justify-center gap-2"
                        >
                          <MessageSquare className="w-4 h-4" />
                          <span className="text-sm">Chat</span>
                        </button>
                        <button
                          onClick={() => onStartCall?.(user, 'voice')}
                          className="p-2 rounded-lg bg-green-500/20 hover:bg-green-500/30 text-green-500"
                          title="Voice Call"
                        >
                          <Phone className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => onStartCall?.(user, 'video')}
                          className="p-2 rounded-lg bg-blue-500/20 hover:bg-blue-500/30 text-blue-500"
                          title="Video Call"
                        >
                          <Video className="w-4 h-4" />
                        </button>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            );
          })}

          {onlineUsers.length === 0 && (
            <div className="text-center py-12 text-[var(--text-muted)]">
              <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No users online</p>
            </div>
          )}
        </div>
      )}

      {/* Hidden audio element for voicemail playback */}
      <audio ref={audioRef} className="hidden" />

      {/* Ticket Detail Modal */}
      <AnimatePresence>
        {showTicketModal && selectedTicket && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-[var(--background-paper)] rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6 border-b border-[var(--border-subtle)]">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-xl font-bold">{selectedTicket.subject}</h3>
                    <p className="text-sm text-[var(--text-muted)] mt-1">
                      From: {selectedTicket.user_name} ({selectedTicket.user_role})
                    </p>
                  </div>
                  <button onClick={() => setShowTicketModal(false)}>
                    <X className="w-6 h-6" />
                  </button>
                </div>
              </div>
              
              <div className="p-6 space-y-4">
                <div className="flex items-center gap-4 flex-wrap">
                  <span className={`px-3 py-1 rounded-full text-sm ${getPriorityColor(selectedTicket.priority)} text-white`}>
                    {selectedTicket.priority?.toUpperCase()}
                  </span>
                  <span className={`text-sm capitalize ${getStatusColor(selectedTicket.status)}`}>
                    {selectedTicket.status?.replace('_', ' ')}
                  </span>
                  <span className="text-sm text-[var(--text-muted)]">
                    {formatDateTime(selectedTicket.created_at)}
                  </span>
                </div>
                
                <div className="bg-[var(--background-elevated)] rounded-lg p-4">
                  <p className="whitespace-pre-wrap">{selectedTicket.message}</p>
                </div>
                
                {selectedTicket.callback_requested && (
                  <div className="flex items-center gap-2 p-3 bg-orange-500/10 rounded-lg text-orange-500">
                    <PhoneCall className="w-5 h-5" />
                    <span>Callback requested via {selectedTicket.preferred_contact}</span>
                    <button
                      onClick={() => {
                        if (selectedTicket.preferred_contact === 'call') {
                          onStartCall?.({ user_id: selectedTicket.user_id, user_name: selectedTicket.user_name }, 'voice');
                        } else {
                          onStartChat?.({ user_id: selectedTicket.user_id, user_name: selectedTicket.user_name });
                        }
                      }}
                      className="ml-auto px-3 py-1 bg-orange-500 text-white rounded-lg text-sm"
                    >
                      {selectedTicket.preferred_contact === 'call' ? 'Call Now' : 'Start Chat'}
                    </button>
                  </div>
                )}
                
                {/* Notes history */}
                {selectedTicket.notes?.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="font-medium">Notes</h4>
                    {selectedTicket.notes.map((note, idx) => (
                      <div key={note.id || idx} className="bg-[var(--background-elevated)] rounded-lg p-3 text-sm">
                        <p>{note.content}</p>
                        <p className="text-xs text-[var(--text-muted)] mt-1">
                          {note.added_by_name} • {formatDateTime(note.added_at)}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Response form */}
                <div className="space-y-3">
                  <Label>Send Response</Label>
                  <Textarea
                    value={ticketResponse}
                    onChange={(e) => setTicketResponse(e.target.value)}
                    placeholder="Type your response..."
                    rows={4}
                  />
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="outline"
                      onClick={() => setShowTicketModal(false)}
                    >
                      Close
                    </Button>
                    <Button
                      onClick={handleSendResponse}
                      disabled={!ticketResponse.trim() || sendingResponse}
                      className="btn-primary"
                    >
                      {sendingResponse ? (
                        <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <Send className="w-4 h-4 mr-2" />
                      )}
                      Send Response
                    </Button>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SupportCenterTab;
